import { UboResponse, UBORoles, useBoundStore } from '@Store/index.ts';
import { OnboardingFormFields } from '@Types/index.ts';
import { ExtendedUseFormReturn, useFormHook } from '@Hooks/index';
import { useSetError } from '@Hooks/index';
import { OwnerFormScheme } from '@Pages/happyFlowPages/addOwner/OwnerFormScheme.ts';
import { IHomeAddressForm } from '@Pages/happyFlowPages/homeAddress/homeAddressForm/HomeAddressForm.tsx';
import { useSaveOwner } from '@Hooks/api/useSaveOwner';
import type { SaveOwnerBody } from '@Types/owner';
import { useSearch } from '@tanstack/react-router';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { formatPhoneForMaskedDefaultValue, formatPhoneNumberForStore } from '@Services/index';
import { useMemo } from 'react';
import { format } from 'date-fns';
import {
  getParsedDateAsISO,
  normalizeBirthDateInput,
  normalizeSsnInput,
} from '@Services/utils/utils.ts';
import {
  getStateDropdownItem,
  useHandleOnCheckedChange,
} from '@Components/inputs/AddressStateDropDown';
import { useAddOwnerAnalytics } from '@Pages/happyFlowPages/addOwner/AddOwnerAnalytics.tsx';

export interface OwnerFormFields extends IHomeAddressForm {
  [OnboardingFormFields.FIRST_NAME]: string;
  [OnboardingFormFields.LAST_NAME]: string;
  [OnboardingFormFields.PHONE_NUMBER]: string;
  [OnboardingFormFields.EMAIL]: string;
  [OnboardingFormFields.SSN]: string;
  [OnboardingFormFields.DATE_OF_BIRTH]: string;
  [OnboardingFormFields.OWNER_PERCENTAGE]: number;
  [OnboardingFormFields.OWNER_ACCESS_GRANTED]: boolean;
  [OnboardingFormFields.OWNER_SAME_AS_PRIMARY]: boolean;
}
export const useAddOwnerLogic = (ubo?: UboResponse) => {
  const { mutateAsync: saveOwner, isPending: isSavingOwner } = useSaveOwner();
  const params: { uboId?: string } = useSearch({ strict: false });
  const { navigateToSpecificPage } = useNavigationManager();
  const { reportSetUBO } = useAddOwnerAnalytics();
  const getCustomerData = useBoundStore((state) => state.getCustomerData);
  const personalAddress = getCustomerData().customerData.customerAddressData;

  //TODO: Add logic that check if how much percentage can be added to the ubo depending on the rest of the ubos

  const userInitialRole = (): boolean => {
    if (params.uboId && ubo?.role) {
      return ubo.role === UBORoles.PARTNER_FULL_ACCESS;
    }
    return true;
  };

  const defaultValues: OwnerFormFields = useMemo(() => {
    return {
      [OnboardingFormFields.LINE1]: (params.uboId && ubo?.customerAddressData?.line1) || '',
      [OnboardingFormFields.APARTMENT]: (params.uboId && ubo?.customerAddressData?.line2) || '',
      [OnboardingFormFields.CITY]: (params.uboId && ubo?.customerAddressData?.city) || '',
      [OnboardingFormFields.STATE]:
        (params.uboId && getStateDropdownItem(ubo?.customerAddressData?.state)) || null,
      [OnboardingFormFields.ZIP]: (params.uboId && ubo?.customerAddressData?.postalCode) || '',
      [OnboardingFormFields.FIRST_NAME]: (params.uboId && ubo?.firstName) || '',
      [OnboardingFormFields.LAST_NAME]: (params.uboId && ubo?.lastName) || '',

      [OnboardingFormFields.PHONE_NUMBER]:
        (params.uboId && ubo?.phoneNumber && formatPhoneForMaskedDefaultValue(ubo?.phoneNumber)) ||
        '',
      [OnboardingFormFields.EMAIL]: (params.uboId && ubo?.email) || '',
      [OnboardingFormFields.SSN]: (params.uboId && ubo?.ssn) || '',
      [OnboardingFormFields.DATE_OF_BIRTH]:
        (params.uboId && ubo?.birthDate && format(new Date(ubo?.birthDate), 'MM/dd/yyyy')) || '',
      [OnboardingFormFields.OWNER_PERCENTAGE]: (params.uboId && ubo?.ownershipPercent) || 25,
      [OnboardingFormFields.OWNER_ACCESS_GRANTED]: userInitialRole(),
      [OnboardingFormFields.OWNER_SAME_AS_PRIMARY]:
        (params.uboId && ubo?.customerAddressData?.sameAsPrimary) || false,
    };
  }, [ubo, params.uboId]);

  const formMethods: ExtendedUseFormReturn<OwnerFormFields> = useFormHook({
    formSchema: OwnerFormScheme,
    formDefaultValues: defaultValues,
    schemaTitle: 'ownerForm',
    values: defaultValues,
  });
  const { watch, trigger, setValue } = formMethods;
  const shouldUsePersonalAddress = watch(OnboardingFormFields.OWNER_SAME_AS_PRIMARY);
  const line1 = personalAddress ? personalAddress.line1 : '';
  const city = personalAddress ? personalAddress.city : '';
  const postalCode = personalAddress ? personalAddress.postalCode : '';
  const line2 = personalAddress ? personalAddress.line2 : '';
  const state = personalAddress ? personalAddress.state : '';

  const { handleOnCheckChange } = useHandleOnCheckedChange({
    setValue,
    state: state,
    line1,
    city,
    postalCode,
    line2,
    trigger,
    formMethods,
    defaultValues,
  });

  const prepareBody = () => {
    const values = formMethods.getValues();
    const requestBody: SaveOwnerBody = {
      ...(params.uboId && { externalId: params.uboId }),
      email: values[OnboardingFormFields.EMAIL],
      phoneNumber: formatPhoneNumberForStore(values[OnboardingFormFields.PHONE_NUMBER]),
      customerPersonalInfo: {
        firstName: values[OnboardingFormFields.FIRST_NAME],
        lastName: values[OnboardingFormFields.LAST_NAME],
      },
      customerSSN: {
        ssn: normalizeSsnInput(values[OnboardingFormFields.SSN]),
        birthDate: normalizeBirthDateInput(
          getParsedDateAsISO(values[OnboardingFormFields.DATE_OF_BIRTH])
        ),
      },
      customerAddress: {
        city: values[OnboardingFormFields.CITY],
        state: values[OnboardingFormFields.STATE]?.value,
        line1: values[OnboardingFormFields.LINE1],
        line2: values[OnboardingFormFields.APARTMENT],
        postalCode: values[OnboardingFormFields.ZIP],
        sameAsPrimary: values[OnboardingFormFields.OWNER_SAME_AS_PRIMARY],
      },
      role: values[OnboardingFormFields.OWNER_ACCESS_GRANTED]
        ? UBORoles.PARTNER_FULL_ACCESS
        : UBORoles.PARTNER,
      ownershipPercent: Number(values[OnboardingFormFields.OWNER_PERCENTAGE]),
    };

    return requestBody;
  };

  const onBackClick = () => {
    navigateToSpecificPage(RoutesMap.OWNER_CENTER);
  };
  const handleSubmit = () => {
    const requestBody = prepareBody();
    saveOwner(requestBody).then(() => onUBOAdded(requestBody));
  };

  const onUBOAdded = (requestBody: SaveOwnerBody) => {
    if (!params.uboId) {
      // Report only on Adding an owner
      const uboListCount =
        getCustomerData().customerData.customerBusinessData.businessUbos?.uboResponseList?.length ||
        0;
      const isOnlyUBO = uboListCount === 0;
      reportSetUBO(requestBody.ownershipPercent, isOnlyUBO, uboListCount);
    }

    navigateToSpecificPage(RoutesMap.OWNER_CENTER);
  };

  useSetError({ formMethods });

  return {
    formMethods,
    onBackClick,
    handleSubmit,
    isLoading: isSavingOwner,
    isSameAddress: shouldUsePersonalAddress,
    handleOnCheckChange,
  };
};
